/**
 * use-ops-shelf-popup — 书架推荐（ADM-OPS-S009）+ 运营弹窗场景（ADM-OPS-S007）+ 弹窗 Paywall 落点（ADM-OPS-S008）读写 hooks
 *
 * 书架推荐两层：appId==='all' 读全局层（省略 app_id）；具体 App 读该 App 层。
 *   - 书架推荐：GET /shelf-recommendations、PUT（root/admin/operator）、DELETE /:id（root/admin）。
 *     读 hook 消费带 `config_source` 的响应壳并向 UI 暴露 items（含 JOIN novels 展示列 + 推荐语池）。
 *   - 运营弹窗场景：GET /popup-scenes（返 §0.3 壳）、POST / PATCH /:id / DELETE /:id（均 root/admin）。
 *     读 hook 返回原始 OperationalPopupSceneResponse[]（scenario 闭集 + iap/stripe product_id + is_active）。
 *   - 弹窗 Paywall 落点：GET /popup-placements（offset 壳，viewer+，只读）。读 hook 经 ops-adapter 塑形成
 *     按结算通道闭集（`SETTLEMENT_CHANNELS`）逐行；**无全局层**（行落 app_payment_methods，app_id 非空）。写入口在支付页
 *     PUT /payment-pages/:id/paywalls 的 operational_popup 分支（A8 §7.5），本文件不承载落点写。
 *
 * mutation 一致策略（checklist）：onSuccess adapt(DTO) → setQueryData 合并（乐观回填，无需等 refetch 闪烁）
 *   → 再 await invalidateQueries 对账；onError 不动 cache（写失败本就没改 cache）但仍 await invalidate 对账，
 *   保证本地与服务端真值一致（如 server 侧并发改了别的行）。pending 期由调用方据 isPending disabled 控件。
 *
 * 稳定空 fallback（checklist #1）：模块级常量数组，禁每渲染新建 `?? []`（下游 effect 死循环）。
 */

import type {
  CreateOperationalPopupSceneRequest,
  CreateShelfRecommendationRequest,
  OperationalPopupSceneResponse,
  OpsConfigSource,
  PopupSceneEnvelopeItem,
  PopupScenesResponse,
  ShelfRecommendationResponse,
  ShelfRecommendationsResponse,
  UpdateOperationalPopupSceneRequest,
  UpdateShelfRecommendationRequest,
} from '@novel-admin/shared';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppScope } from '~/app/app-scope-store';
import {
  type PopupPlacementChannelRow,
  toPopupPlacementChannelRows,
} from '~/data/adapters/ops-adapter';
import { useApps } from '~/data/queries';
import { opsApi } from '~/hooks/use-ops';

// 稳定空 fallback 常量（禁每渲染 `?? []` 新建空数组触发死循环）
const SHELF_RECS_FALLBACK: ShelfRecommendationResponse[] = [];
const POPUP_SCENES_FALLBACK: PopupSceneEnvelopeItem[] = [];
// 落点未就绪时的稳定空态：结算通道闭集逐行、全部「未配置」（不是空表 —— 渠道行本来就恒存在）
const POPUP_PLACEMENT_FALLBACK: PopupPlacementChannelRow[] = toPopupPlacementChannelRows([]);

/**
 * App scope 是否为「全局层」视图。data 层不 import `~/pages/ops/ops-scope`（页面层）的同名
 * helper，保持依赖单向；判据一致：空 / `'all'` = 全局层。
 */
const isGlobalScope = (appId: string): boolean => !appId || appId === 'all';

// ---------- 书架推荐（S009）----------
/**
 * 某 App（或聚合）下书架推荐列表（接真）。
 * queryKey ['shelf-recommendations', appId]：与下方 upsert/delete mutation 失效 key 对齐。
 *   - 具体 App scope → listShelfRecommendations(appId) 单次取该 App 全列表（server 按 sort_order ASC 排序）；
 *   - 'all' → 读全局层（省略 app_id）；具体 App → 读该 App 层。
 */
export function useShelfRecommendations(
  scopeAppId?: string,
  language?: string,
): {
  data: ShelfRecommendationResponse[];
  configSource: 'GLOBAL' | 'GLOBAL_INHERITED' | 'APP_DEDICATED';
  isLoading: boolean;
} {
  const storeAppId = useAppScope((s) => s.appId);
  const appId = scopeAppId ?? storeAppId;
  const q = useQuery({
    queryKey: ['shelf-recommendations', appId, language ?? ''],
    enabled: Boolean(language),
    queryFn: async () => {
      if (!language) throw new Error('language is required');
      if (appId === 'all') return opsApi.listShelfRecommendations(undefined, language);
      return opsApi.listShelfRecommendations(appId, language);
    },
  });
  return {
    data: q.data?.items ?? SHELF_RECS_FALLBACK,
    configSource: q.data?.config_source ?? (appId === 'all' ? 'GLOBAL' : 'GLOBAL_INHERITED'),
    isLoading: q.isLoading,
  };
}

/**
 * 书架推荐写动作（upsert PUT + delete DELETE）mutation 集。
 * appId 入 setQueryData / invalidate 的 key（与读 hook 对齐）；聚合视图无写动作，故 mutation 仅在具体 App 视图调用。
 */
export function useShelfRecommendationMutations(scopeAppId?: string, language?: string) {
  const queryClient = useQueryClient();
  const storeAppId = useAppScope((s) => s.appId);
  const appId = scopeAppId ?? storeAppId;
  const key = ['shelf-recommendations', appId, language ?? ''] as const;

  /** 写成功后把 DTO 合并回 envelope（乐观回填，减少 invalidate 前的空窗闪烁）。 */
  const mergeRow = (dto: ShelfRecommendationResponse) => {
    // 乐观回填：按 id 合并（既有则替换，否则追加）→ 减少 invalidate 前的空窗闪烁
    queryClient.setQueryData<ShelfRecommendationsResponse>(key, (prev) => {
      const envelope =
        prev ??
        ({
          config_source: appId === 'all' ? 'GLOBAL' : 'APP_DEDICATED',
          app_id: appId === 'all' ? null : appId,
          language: language ?? '',
          items: [],
        } satisfies ShelfRecommendationsResponse);
      const list = envelope.items;
      const idx = list.findIndex((r) => r.id === dto.id);
      return {
        ...envelope,
        items: idx >= 0 ? list.map((row) => (row.id === dto.id ? dto : row)) : [...list, dto],
      };
    });
  };

  const create = useMutation<ShelfRecommendationResponse, Error, CreateShelfRecommendationRequest>({
    mutationFn: (body) => opsApi.createShelfRecommendation(body),
    onSuccess: async (dto) => {
      mergeRow(dto);
      await queryClient.invalidateQueries({ queryKey: ['shelf-recommendations'] });
    },
    onError: async () => {
      // 写失败未改 cache，但仍对账（server 侧可能因并发改了别行）
      await queryClient.invalidateQueries({ queryKey: ['shelf-recommendations'] });
    },
  });

  const update = useMutation<
    ShelfRecommendationResponse,
    Error,
    { id: string; body: UpdateShelfRecommendationRequest }
  >({
    mutationFn: ({ id, body }) => opsApi.updateShelfRecommendation(id, body),
    onSuccess: async (dto) => {
      mergeRow(dto);
      await queryClient.invalidateQueries({ queryKey: ['shelf-recommendations'] });
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: ['shelf-recommendations'] });
    },
  });

  const remove = useMutation<{ ok: true }, Error, string>({
    mutationFn: (id) => opsApi.deleteShelfRecommendation(id),
    onSuccess: async (_res, id) => {
      queryClient.setQueryData<ShelfRecommendationsResponse>(key, (prev) =>
        prev ? { ...prev, items: prev.items.filter((row) => row.id !== id) } : prev,
      );
      await queryClient.invalidateQueries({ queryKey: ['shelf-recommendations'] });
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: ['shelf-recommendations'] });
    },
  });

  return { create, update, remove };
}
export type ShelfRecommendationMutations = ReturnType<typeof useShelfRecommendationMutations>;

// ---------- 运营弹窗场景（S007）----------
/**
 * 运营弹窗场景列表（canonical `GET /ops/popup-scenes`，dev-docs `02-API契约/A2-运营配置.md` §5.1）。
 *
 * **两层单层选取**（不并集、不逐条覆盖）：
 *   - `appId === 'all'` → 省 app_id 读**全局层**（`app_id IS NULL`），`config_source='GLOBAL'`。
 *     旧实现在这里直接返空数组、根本不发请求，于是全局层行在后台永远不可见 —— 本 hook 改为真读。
 *   - 具体 App 且该 App 有未软删行 → 只读该层，`config_source='APP_DEDICATED'`。
 *   - 具体 App 且零行 → `config_source='GLOBAL_INHERITED'` 且 `items` 恒 `[]`（服务端不回传全局行）。
 *
 * **无语言维度**（§5 头部）：queryKey 不含 language，切语言不影响本查询——一个 App 的上下架对其
 * 全部语言一致（张培 dev-docs#733 PR733-007 拍板推翻了早期的「App × 语言」口径）。
 */
export function useOperationalPopupScenes(scopeAppId?: string): {
  data: PopupSceneEnvelopeItem[];
  /** 取数未就绪（loading / error）时为 null —— **不伪造层状态**，调用方据此避免向运营断言 */
  configSource: OpsConfigSource | null;
  isLoading: boolean;
  /** 取数失败：调用方据此禁用新建（此时 data 是空 fallback，不代表「该 App 零场景」）*/
  isError: boolean;
} {
  const storeAppId = useAppScope((s) => s.appId);
  const appId = scopeAppId ?? storeAppId;
  const q = useQuery({
    queryKey: ['popup-scenes', appId],
    queryFn: () => opsApi.listPopupScenes(isGlobalScope(appId) ? null : appId),
  });
  return {
    data: q.data?.items ?? POPUP_SCENES_FALLBACK,
    // 未加载完 / 取数失败 → null。此前按 scope 伪造 GLOBAL / GLOBAL_INHERITED，下游把它当权威值，
    // 于是网络故障会被渲染成「该 App 没有覆盖层」「C 端读全局层那一份」这类配置事实断言。
    configSource: q.data?.config_source ?? null,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}

/** 全局层读者判定用的 App 摘要（只取展示所需两字段，不透传整行）。 */
export type PopupLayerAppBrief = { id: string; name: string };

/**
 * 全局层的**真实读者**：当前哪些 App 因零覆盖行而按 §5.1 回落读全局层。
 *
 * 为什么要算这个：全局层的上下架**只对零行的 App 生效**，而「该 App 有行」是单调状态
 * （§5.4 禁止删到零行，且本组端点不提供退回全局层的路径）—— 一旦全部在营 App 都建了自己的
 * 覆盖层，全局层对线上就是零影响，只剩「新建 App 的默认值」一个用途。界面若只写「没有自己
 * 覆盖层的 App 全读这一份」，运营会把它读成总开关，拨完开关以为已经生效。故把条件句换成实数。
 *
 * 判据取服务端返回的 `config_source`（§5.1 三值闭集），不是前端自己数行数：`GLOBAL_INHERITED`
 * = 该 App 零行 → 读全局层；`APP_DEDICATED` = 该 App 有行 → 与全局层无关。
 *
 * 参与判定的集合 = `listApps()` 的返回，服务端已硬排除系统保留行（`is_system=true` 的 sentinel
 * App），故这里的计数与 scope bar 上运营看得见的 App 按钮一一对应，不会把非产品 App 算进来。
 *
 * queryKey 与 `useOperationalPopupScenes` 逐字一致，**共享同一份 cache**：本 hook 预热的每个
 * App 层，运营切过去时直接命中，不额外多打一轮请求。
 *
 * **不就绪不断言**：App 列表或任一 App 的场景查询仍在加载 / 失败 → 两个返回值都是 `null`，
 * 调用方据此显示中性态。把「还没查到」渲染成「0 个 App 读这一层」会让运营据一个假事实决策。
 */
export function useGlobalPopupLayerReaders(enabled: boolean): {
  /** 零覆盖行、当前真读全局层的 App；`null` = 未就绪 / 有失败，**不向运营断言** */
  readers: PopupLayerAppBrief[] | null;
  /** 已有自己覆盖层、不读全局层的 App；`null` 同上（与 `readers` 同生同灭）*/
  covered: PopupLayerAppBrief[] | null;
} {
  const { data: apps, isLoading: appsLoading, isError: appsError } = useApps();
  const results = useQueries({
    queries: apps.map((a) => ({
      queryKey: ['popup-scenes', a.id],
      queryFn: () => opsApi.listPopupScenes(a.id),
      enabled,
    })),
  });

  if (!enabled || appsLoading || appsError) return { readers: null, covered: null };
  if (results.some((r) => r.isPending || r.isError)) return { readers: null, covered: null };
  // useQueries 的返回与入参逐位对应；长度对不上说明这一帧还没对齐，宁可不断言。
  if (results.length !== apps.length) return { readers: null, covered: null };

  const readers: PopupLayerAppBrief[] = [];
  const covered: PopupLayerAppBrief[] = [];
  apps.forEach((a, i) => {
    const brief = { id: a.id, name: a.name };
    // GLOBAL_INHERITED 是「零行」的唯一权威信号；其余取值（含理论上不该出现的 GLOBAL）一律
    // 不计入读者 —— 宁可少报一个读者，也不谎报「改这里会影响它」。
    (results[i]?.data?.config_source === 'GLOBAL_INHERITED' ? readers : covered).push(brief);
  });
  return { readers, covered };
}

/**
 * 运营弹窗场景写动作（canonical POST / PATCH / DELETE，A2 §5.2~§5.4）mutation 集。
 *
 * 旧实现是单个「全字段 PUT upsert」；canonical 拆成 CRUD 语义后：
 *   - `create` = POST，服务端定 `is_active=false`（新建即下架，运营再显式上架）；
 *   - `update` = PATCH `:id`，**只传要改的字段**，不再回传整行 —— 因此并发下不会再出现
 *     stale 全 PUT 把别人刚改的字段静默恢复的窗口（旧实现靠 `popupRow` 从 cache 取实时行来兜）。
 */
export function useOperationalPopupSceneMutations(scopeAppId?: string) {
  const queryClient = useQueryClient();
  const storeAppId = useAppScope((s) => s.appId);
  const appId = scopeAppId ?? storeAppId;
  const key = ['popup-scenes', appId] as const;

  /** 写成功后把 DTO 合并回当前 scope 的 envelope（乐观回填，避免等 refetch 闪烁）。 */
  const mergeRow = (dto: OperationalPopupSceneResponse) => {
    queryClient.setQueryData<PopupScenesResponse>(key, (prev) => {
      if (!prev) return prev;
      const idx = prev.items.findIndex((s) => s.id === dto.id);
      const items =
        idx >= 0 ? prev.items.map((s) => (s.id === dto.id ? dto : s)) : [...prev.items, dto];
      // 该 App 建出第一行即转入 APP_DEDICATED（§5.2「无身份行前置」）
      const config_source = prev.config_source === 'GLOBAL' ? 'GLOBAL' : 'APP_DEDICATED';
      return { ...prev, items, config_source };
    });
  };

  const create = useMutation<
    OperationalPopupSceneResponse,
    Error,
    CreateOperationalPopupSceneRequest
  >({
    mutationFn: (body) => opsApi.createPopupScene(body),
    onSuccess: async (dto) => {
      mergeRow(dto);
      await queryClient.invalidateQueries({ queryKey: ['popup-scenes'] });
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: ['popup-scenes'] });
    },
  });

  const update = useMutation<
    OperationalPopupSceneResponse,
    Error,
    { id: string; body: UpdateOperationalPopupSceneRequest }
  >({
    mutationFn: ({ id, body }) => opsApi.updatePopupScene(id, body),
    onSuccess: async (dto) => {
      mergeRow(dto);
      await queryClient.invalidateQueries({ queryKey: ['popup-scenes'] });
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: ['popup-scenes'] });
    },
  });

  const remove = useMutation<{ id: string; deleted_at: string }, Error, string>({
    mutationFn: (id) => opsApi.deletePopupScene(id),
    onSuccess: async (_res, id) => {
      queryClient.setQueryData<PopupScenesResponse>(key, (prev) =>
        prev ? { ...prev, items: prev.items.filter((s) => s.id !== id) } : prev,
      );
      await queryClient.invalidateQueries({ queryKey: ['popup-scenes'] });
    },
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: ['popup-scenes'] });
    },
  });

  return { create, update, remove };
}
export type OperationalPopupSceneMutations = ReturnType<typeof useOperationalPopupSceneMutations>;

// ---------- 运营弹窗 Paywall 落点（S008）----------
/**
 * 当前 App 的运营弹窗 Paywall 落点（`GET /ops/popup-placements`，A2 §6.1）。
 *
 * **无全局层**：落点行落在 `app_payment_methods`，`app_id` 非空 —— 聚合视图（`'all'`）下没有可配的行，
 * 故这里 `enabled=false`、不发请求，由调用方渲染「请先选具体 App」而不是画一张假表。
 *
 * 响应是 offset 分页壳（每个结算通道至多一行，不做翻页 UI），经 `toPopupPlacementChannelRows` 塑形成
 * 按结算通道闭集逐行的渠道视图：未建行 / 建了行但未填值都归「未配置」，与 C 端「不参与 serving」同口径。
 *
 * `nativeAppConfirmed`：落点只服务已确认的原生 App（A8 §8 仅 IOS / ANDROID 接入 Adapty，服务端对
 * WEB 拒绝）。平台未确认或为 WEB 时调用方传 false，不发请求。
 */
export function usePopupPlacements(
  scopeAppId?: string,
  nativeAppConfirmed = true,
): {
  data: PopupPlacementChannelRow[];
  isLoading: boolean;
  /** 取数失败：调用方据此禁写（此时 data 是「全未配置」的空态，不代表真的没配）*/
  isError: boolean;
  /** fail-closed 就绪位：真拿到数据才为 true —— 离线 / 请求挂起时 isLoading 与 isError 同为 false，
   *  只用那两个判就绪会把空态 fallback 当真值上屏并放行写。*/
  isSuccess: boolean;
  /** 该 scope 是否有落点可展示（聚合视图恒 false）*/
  enabled: boolean;
} {
  const storeAppId = useAppScope((s) => s.appId);
  const appId = scopeAppId ?? storeAppId;
  const enabled = !isGlobalScope(appId);
  const queryEnabled = enabled && nativeAppConfirmed;
  const q = useQuery({
    queryKey: ['popup-placements', appId],
    enabled: queryEnabled,
    queryFn: () => opsApi.listPopupPlacements(appId),
  });
  return {
    data: q.data ? toPopupPlacementChannelRows(q.data.data) : POPUP_PLACEMENT_FALLBACK,
    isLoading: queryEnabled && q.isLoading,
    isError: q.isError,
    // fail-closed 就绪位：只有真拿到数据才算就绪。离线 / 请求被挂起时 status='pending' +
    // fetchStatus='paused'，isLoading 与 isError 同时为 false，消费方若用 `isLoading || isError`
    // 判就绪会把空态 fallback 当真值上屏并放行写。
    isSuccess: queryEnabled && q.isSuccess,
    enabled,
  };
}
